'use client';

/**
 * ConfirmModal — confirmare destructivă (Șterge / Dezactivează / etc.)
 *
 * Diferit față de `ConfirmDialog` din `@softfactura/ui`:
 *  - `body: ReactNode` (nu doar string description) — permite `<b>`, link-uri, etc.
 *  - `actionLabel + danger` (vs `confirmText + variant`) — pattern shorthand
 *  - role="alertdialog" + aria-modal — semantic corect pentru confirmări critice
 *  - Auto-focus pe Cancel (best practice — evită Enter accidental → delete)
 *  - Stilizare `.v-modal` consistent cu restul (nu Tailwind cards)
 *
 * Restored 2026-05-05 după ce a fost ștearsă accidental într-un script de cleanup;
 * 9 list-views o referenția fără să o aibă definită.
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useDialog } from '@/shared/hooks/use-dialog';

interface ConfirmModalProps {
  title: string;
  body: ReactNode;
  actionLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  title,
  body,
  actionLabel = 'Confirmă',
  cancelLabel = 'Anulează',
  danger,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Auto-focus Cancel — best practice pentru destructive actions
  // (Enter accidentally pressed should NOT trigger Confirm).
  useEffect(() => {
    const t = setTimeout(() => cancelRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  // useDialog: focus trap + Esc + focus return. autoFocus=false ca sa pastreze
  // auto-focus pe Cancel (best practice destructive — useEffect de mai sus).
  const dialogRef = useDialog({ isOpen: true, onClose: onCancel, autoFocus: false });

  return (
    <div
      className="v-modal-backdrop"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="v-modal"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-body"
      >
        <div className="v-modal-head">
          <AlertTriangle size={18} style={{ color: danger ? '#DC2626' : '#D97706' }} />
          <h3 id="confirm-modal-title">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Închide"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#8E8B83' }}
          >
            <X size={16} />
          </button>
        </div>
        <div
          id="confirm-modal-body"
          style={{ padding: '14px 18px', fontSize: '0.8125rem', color: '#3A3934', lineHeight: 1.5 }}
        >
          {body}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            padding: '12px 18px',
            borderTop: '1px solid var(--v-border)',
            background: 'var(--v-bg-deeper)',
          }}
        >
          <button ref={cancelRef} type="button" className="v-btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="v-btn"
            onClick={onConfirm}
            style={{
              background: danger ? '#DC2626' : 'rgb(var(--primary-500))',
              color: 'white',
              borderColor: 'transparent',
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
